/**
 * Crop constraint utilities for the avatar crop feature.
 *
 * Pure functions that compute zoom bounds, clamp drag positions, and
 * generate default center-crop parameters. Shared between web and mobile.
 */

/** Crop area in original-image pixel coordinates. */
export interface CropParams {
  /** X offset of crop area from top-left of image (pixels at original resolution) */
  x: number;
  /** Y offset of crop area from top-left of image (pixels at original resolution) */
  y: number;
  /** Width of crop area in pixels at original resolution */
  width: number;
  /** Height of crop area in pixels at original resolution */
  height: number;
}

export interface Position {
  x: number;
  y: number;
}

export interface ZoomConstraints {
  /** Minimum zoom where image fills the crop frame */
  minZoom: number;
  /** Maximum zoom (5× the minimum) */
  maxZoom: number;
}

/**
 * Computes zoom constraints given image dimensions and crop frame size.
 *
 * `minZoom` is the smallest scale factor at which the image still completely
 * fills the square crop frame (no empty space). This is determined by the
 * shorter dimension of the image relative to the frame.
 *
 * `maxZoom` is always 5× the minimum zoom.
 *
 * Requirements: 3.3, 3.4
 */
export function computeZoomConstraints(
  imageWidth: number,
  imageHeight: number,
  frameSize: number,
): ZoomConstraints {
  // minZoom ensures the shorter scaled dimension fills the frame.
  // At zoom z, the displayed image size is (imageWidth * z, imageHeight * z).
  // Both must be >= frameSize, so z >= frameSize / imageWidth AND z >= frameSize / imageHeight.
  const minZoom = Math.max(frameSize / imageWidth, frameSize / imageHeight);
  const maxZoom = minZoom * 5;

  return { minZoom, maxZoom };
}

/**
 * Clamps a drag position so the image always fills the crop frame.
 *
 * The position represents the top-left corner of the image relative to the
 * crop frame's top-left corner (in display/screen coordinates). At any valid
 * position, the image must extend at least to all edges of the frame.
 *
 * Constraints:
 * - The image's left edge must be at or left of the frame's left edge: x <= 0
 * - The image's top edge must be at or above the frame's top edge: y <= 0
 * - The image's right edge must be at or right of the frame's right edge:
 *   x + imageWidth * zoom >= frameSize → x >= -(imageWidth * zoom - frameSize)
 * - The image's bottom edge must be at or below the frame's bottom edge:
 *   y + imageHeight * zoom >= frameSize → y >= -(imageHeight * zoom - frameSize)
 *
 * Requirements: 4.3, 10.3
 */
export function clampPosition(
  position: Position,
  imageWidth: number,
  imageHeight: number,
  zoom: number,
  frameSize: number,
): Position {
  const scaledWidth = imageWidth * zoom;
  const scaledHeight = imageHeight * zoom;

  // Maximum offset is 0 (image edge aligns with frame edge)
  // Minimum offset is -(scaledDimension - frameSize) (opposite edge aligns)
  const minX = -(scaledWidth - frameSize);
  const maxX = 0;
  const minY = -(scaledHeight - frameSize);
  const maxY = 0;

  return {
    x: Math.min(maxX, Math.max(minX, position.x)),
    y: Math.min(maxY, Math.max(minY, position.y)),
  };
}

/**
 * Computes a default center crop for an image.
 *
 * Returns a square CropParams centered on the image, with side length equal
 * to the shorter dimension. Used when a user cancels the crop modal during
 * initial upload or when a legacy user first opens the crop tool.
 *
 * Requirements: 8.3, 12.2
 */
export function computeDefaultCenterCrop(
  imageWidth: number,
  imageHeight: number,
): CropParams {
  const size = Math.min(imageWidth, imageHeight);
  return {
    x: Math.floor((imageWidth - size) / 2),
    y: Math.floor((imageHeight - size) / 2),
    width: size,
    height: size,
  };
}
